import { Page_Size } from '../../../../../Components/helper';
import {
  MyTodoState,
  MyTodoAction,
  MyTodoActionTypes,
  MyTodoFilterValues,
  IMyTodoListRow,
  IMyTodo,
  MY_TODO_FILTER_STORAGE_KEY,
} from './my-todo.types';

function getInitialFilterValues(): MyTodoFilterValues {
  const defaults: MyTodoFilterValues = {
    view: 'my-todo',
    pageIndex: 1,
    pageSize: Page_Size,
    searchText: '',
    sortOrder: '',
    sortPropertyName: '',
    statuses: [],
  };
  try {
    const raw = localStorage.getItem(MY_TODO_FILTER_STORAGE_KEY);
    if (!raw) return defaults;
    const parsed = JSON.parse(raw) as { filterState?: Record<string, unknown>; activeTab?: string };
    const fs = parsed?.filterState;
    if (!fs) return defaults;
    return {
      ...defaults,
      view: (parsed.activeTab as 'my-todo' | 'all-todo') ?? 'my-todo',
      statuses: (fs.activeStatus as string[]) ?? [],
      brandId: (fs.activeBrand as string[]) ?? [],
      subBrandId: (fs.activeSubBrand as string[]) ?? [],
      productType: (fs.activeProductType as string[]) ?? [],
      catOneId: (fs.activeCat1 as string[]) ?? [],
      catTwoId: (fs.activeCat2 as string[]) ?? [],
      catThreeId: (fs.activeCat3 as string[]) ?? [],
      catFourId: (fs.activeCat4 as string[]) ?? [],
      supplierId: (fs.activeSupplier as string[]) ?? [],
      seasonId: (fs.activeSeason as string[]) ?? [],
      year: (fs.activeYear as string[]) ?? [],
      month: (fs.activeMonth as string[]) ?? [],
      createdBy: (fs.activeCreatedBy as string[]) ?? [],
      productStatus: (fs.productStatus as string[]) ?? [],
      licensorId: (fs.activeLicensor as string[]) ?? [],
      licenseId: (fs.activeLicense as string[]) ?? [],
      likenessId: (fs.activeLikeness as string[]) ?? [],
      colorwayId: (fs.activeColorway as string[]) ?? [],
      colorGroupId: (fs.activeColorGroup as string[]) ?? [],
      dynamicTypes: (fs.dynamicTypes as Array<{ colId: string; valId: string }>) ?? [],
      textCustomAttribute: (fs.textCustomAttribute as string[]) ?? [],
    };
  } catch {
    return defaults;
  }
}

// Initial state
const initialState: MyTodoState = {
  items: [],
  loading: false,
  error: undefined,
  pendingCount: 0,
  allTodoPendingCount: 0,
  totalCount: 0,
  filterValues: getInitialFilterValues(),
  currentRequestId: null,
};

// Reducer
const myTodoReducer = (state = initialState, action: MyTodoAction): MyTodoState => {
  switch (action.type) {
    case MyTodoActionTypes.FETCH_MY_TODOS: {
      if (
        action.payload.requestId &&
        state.currentRequestId &&
        Number(action.payload.requestId) < Number(state.currentRequestId)
      ) {
        return state;
      }
      const { items, totalCount, isScrolled } = action.payload;
      return {
        ...state,
        items: isScrolled ? [...(state.items || []), ...(items || [])] : items || [],
        totalCount: totalCount || 0,
        loading: false,
        error: undefined,
        currentRequestId: action.payload.requestId || state.currentRequestId,
      };
    }

    case MyTodoActionTypes.ADD_MY_TODO: {
      return state;
    }

    case MyTodoActionTypes.UPDATE_MY_TODO_STATUS: {
      return state;
    }

    case MyTodoActionTypes.DELETE_MY_TODO: {
      const { id, ids } = action.payload;
      const deletedSet = new Set(ids ?? (id ? [id] : []));
      if (deletedSet.size === 0) return state;

      const filterTasks = (tasks: IMyTodo[]) =>
        tasks.filter((t) => !deletedSet.has(t._id));

      const updatedItems = state.items
        .map((row) => {
          if (row.task && deletedSet.has(row.task._id)) {
            return null;
          }
          if (row.expandData?.some((t) => deletedSet.has(t._id))) {
            const remaining = filterTasks(row.expandData);
            if (remaining.length === 0) return null;
            return {
              ...row,
              expandData: remaining,
              totalTasks: remaining.length,
              doneTasks: remaining.filter((t) => t.status === 'Done').length,
              hasExpandData: remaining.length > 1,
              task: remaining.length === 1 ? remaining[0] : undefined,
            };
          }
          return row;
        })
        .filter((row): row is IMyTodoListRow => row !== null);

      return {
        ...state,
        items: updatedItems,
        totalCount: Math.max(0, state.totalCount - deletedSet.size),
      };
    }

    case MyTodoActionTypes.SET_LOADING: {
      return { ...state, loading: action.payload };
    }

    case MyTodoActionTypes.FETCH_MY_TODO_COUNT: {
      return {
        ...state,
        pendingCount: action.payload.pendingCount,
        allTodoPendingCount: action.payload.allTodoPendingCount,
      };
    }

    case MyTodoActionTypes.SET_FILTER_VALUES: {
      const viewChanging =
        action.payload.view !== undefined && action.payload.view !== state.filterValues.view;
      return {
        ...state,
        items: viewChanging ? [] : state.items,
        totalCount: viewChanging ? 0 : state.totalCount,
        loading: viewChanging ? true : state.loading,
        filterValues: {
          ...state.filterValues,
          ...action.payload,
        },
      };
    }

    case MyTodoActionTypes.CLEAR_MY_TODO_REDUCER: {
      return initialState;
    }

    default:
      return state;
  }
};

// Selectors
export const getItems = (state: MyTodoState): IMyTodoListRow[] => state.items;
export const getPendingCount = (state: MyTodoState): number => state.pendingCount;
export const getAllTodoPendingCount = (state: MyTodoState): number => state.allTodoPendingCount;
export const getTotalCount = (state: MyTodoState): number => state.totalCount;
export const getFilterValues = (state: MyTodoState): MyTodoFilterValues => state.filterValues;
export const getLoading = (state: MyTodoState): boolean => state.loading;
export const getError = (state: MyTodoState): string | undefined => state.error;

export default myTodoReducer;
